import React, { useState } from 'react';
import {
  Box,
  Typography,
  Paper,
  Grid,
  Avatar,
  Chip,
  Button,
  TextField,
  InputAdornment,
} from '@mui/material';
import SchoolIcon from '@mui/icons-material/School';
import CodeIcon from '@mui/icons-material/Tag';
import EmailIcon from '@mui/icons-material/Email';
import PhoneIcon from '@mui/icons-material/Phone';
import PlaceIcon from '@mui/icons-material/Place';
import EditIcon from '@mui/icons-material/Edit';
import SaveIcon from '@mui/icons-material/Save';
import { useQuery, useMutation, useQueryClient } from 'react-query';
import apiClient from '@uniqcamp/shared/services/api.service';
import { authService } from '@uniqcamp/shared';
import { canAccessPrincipalFeatures } from '../utils/roles';
import { useToast } from '../contexts/ToastContext';
import LockIcon from '@mui/icons-material/Lock';

interface School {
  _id: string;
  name: string;
  uniqueCode: string;
  address?: { street?: string; city?: string; state?: string; zipCode?: string; country?: string };
  registeredMobile?: string;
  registeredEmail?: string;
  contact?: { phone?: string; email?: string; website?: string };
  logo?: string;
  onboardingStatus?: string;
  onboardingCompletedAt?: string;
}

interface SchoolsResponse {
  schools: School[];
}

function formatAddress(addr: School['address']): string {
  if (!addr) return '—';
  const parts = [addr.street, addr.city, addr.state, addr.zipCode, addr.country].filter(Boolean);
  return parts.join(', ') || '—';
}

const emptyAddress = { street: '', city: '', state: '', zipCode: '', country: '' };

const SchoolProfile: React.FC = () => {
  const queryClient = useQueryClient();
  const { showToast } = useToast();
  const user = authService.getStoredUser();
  const canEdit = user && canAccessPrincipalFeatures(user.role);

  const { data, isLoading } = useQuery<SchoolsResponse>('schools', async () => {
    const r = await apiClient.get('/schools');
    return r.data as SchoolsResponse;
  });

  const school = data?.schools?.[0];
  const [editing, setEditing] = useState(false);
  const [changePw, setChangePw] = useState({ currentPassword: '', newPassword: '', confirmPassword: '' });
  const [changePwLoading, setChangePwLoading] = useState(false);
  const [form, setForm] = useState({
    name: '',
    registeredMobile: '',
    registeredEmail: '',
    address: { ...emptyAddress },
    logo: '',
  });

  const syncFormFromSchool = (s: School) => {
    setForm({
      name: s.name || '',
      registeredMobile: s.registeredMobile || s.contact?.phone || '',
      registeredEmail: s.registeredEmail || s.contact?.email || '',
      address: {
        street: s.address?.street || '',
        city: s.address?.city || '',
        state: s.address?.state || '',
        zipCode: s.address?.zipCode || '',
        country: s.address?.country || '',
      },
      logo: s.logo || '',
    });
  };

  React.useEffect(() => {
    if (school) syncFormFromSchool(school);
  }, [school]);

  const updateMutation = useMutation(
    async (payload: typeof form) => {
      const res = await apiClient.put(`/schools/${school!._id}`, {
        name: payload.name.trim(),
        registeredMobile: payload.registeredMobile.trim() || undefined,
        registeredEmail: payload.registeredEmail.trim() || undefined,
        address: payload.address,
        logo: payload.logo || undefined,
      });
      return res.data;
    },
    {
      onSuccess: () => {
        queryClient.invalidateQueries('schools');
        setEditing(false);
        showToast('success', 'Profile updated.');
      },
      onError: (err: unknown) => {
        const e = err as { response?: { data?: { message?: string } } };
        showToast('error', e.response?.data?.message ?? 'Update failed.');
      },
    }
  );

  const handleLogoChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = () => setForm((f) => ({ ...f, logo: (reader.result as string) || '' }));
    reader.readAsDataURL(file);
  };

  const handleChangePassword = async (e: React.FormEvent) => {
    e.preventDefault();
    if (changePw.newPassword !== changePw.confirmPassword) {
      showToast('error', 'New passwords do not match');
      return;
    }
    if (changePw.newPassword.length < 6) {
      showToast('error', 'Password must be at least 6 characters');
      return;
    }
    setChangePwLoading(true);
    try {
      await authService.changePassword(changePw.currentPassword, changePw.newPassword);
      showToast('success', 'Password changed successfully.');
      setChangePw({ currentPassword: '', newPassword: '', confirmPassword: '' });
    } catch (err: unknown) {
      const e = err as { response?: { data?: { message?: string } } };
      showToast('error', e.response?.data?.message ?? 'Failed to change password.');
    } finally {
      setChangePwLoading(false);
    }
  };

  if (isLoading || !school) {
    return (
      <Box sx={{ p: 2 }}>
        <Typography color="text.secondary">{isLoading ? 'Loading…' : 'School not found.'}</Typography>
      </Box>
    );
  }

  const displayMobile = school.registeredMobile || school.contact?.phone || '—';
  const displayEmail = school.registeredEmail || school.contact?.email || '—';

  return (
    <Box sx={{ p: 2 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 2, mb: 2 }}>
        <Typography variant="h5" fontWeight={600}>
          School profile
        </Typography>
        {canEdit && !editing && (
          <Button startIcon={<EditIcon />} variant="contained" onClick={() => setEditing(true)}>
            Edit
          </Button>
        )}
        {canEdit && editing && (
          <Box sx={{ display: 'flex', gap: 1 }}>
            <Button
              startIcon={<SaveIcon />}
              variant="contained"
              onClick={() => updateMutation.mutate(form)}
              disabled={updateMutation.isLoading}
            >
              Save
            </Button>
            <Button
              variant="outlined"
              onClick={() => {
                syncFormFromSchool(school);
                setEditing(false);
              }}
            >
              Cancel
            </Button>
          </Box>
        )}
      </Box>
      <Paper sx={{ p: 3 }}>
        <Grid container spacing={3}>
          <Grid item xs={12} md={3} sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 1 }}>
            {editing ? (
              <>
                {form.logo ? (
                  <Avatar src={form.logo} alt={form.name} sx={{ width: 120, height: 120 }} variant="rounded" />
                ) : (
                  <Avatar sx={{ width: 120, height: 120, bgcolor: 'primary.main' }} variant="rounded">
                    <SchoolIcon sx={{ fontSize: 60 }} />
                  </Avatar>
                )}
                <Typography variant="caption" color="text.secondary">School logo</Typography>
                <input type="file" accept="image/*" onChange={handleLogoChange} />
              </>
            ) : school.logo ? (
              <Avatar src={school.logo} alt={school.name} sx={{ width: 120, height: 120 }} variant="rounded" />
            ) : (
              <Avatar sx={{ width: 120, height: 120, bgcolor: 'primary.main' }} variant="rounded">
                <SchoolIcon sx={{ fontSize: 60 }} />
              </Avatar>
            )}
          </Grid>
          <Grid item xs={12} md={9}>
            {editing ? (
              <Grid container spacing={2}>
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    label="School name"
                    value={form.name}
                    onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))}
                    InputProps={{ startAdornment: <InputAdornment position="start"><SchoolIcon fontSize="small" /></InputAdornment> }}
                  />
                </Grid>
                <Grid item xs={12}>
                  <Typography variant="body2" color="text.secondary">School code: <strong>{school.uniqueCode}</strong> (cannot be changed)</Typography>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    label="Registered mobile"
                    value={form.registeredMobile}
                    onChange={(e) => setForm((f) => ({ ...f, registeredMobile: e.target.value }))}
                    InputProps={{ startAdornment: <InputAdornment position="start"><PhoneIcon fontSize="small" /></InputAdornment> }}
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    label="Registered email"
                    type="email"
                    value={form.registeredEmail}
                    onChange={(e) => setForm((f) => ({ ...f, registeredEmail: e.target.value }))}
                    InputProps={{ startAdornment: <InputAdornment position="start"><EmailIcon fontSize="small" /></InputAdornment> }}
                  />
                </Grid>
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    label="Address (street)"
                    value={form.address.street}
                    onChange={(e) => setForm((f) => ({ ...f, address: { ...f.address, street: e.target.value } }))}
                    InputProps={{ startAdornment: <InputAdornment position="start"><PlaceIcon fontSize="small" /></InputAdornment> }}
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField fullWidth label="City" value={form.address.city} onChange={(e) => setForm((f) => ({ ...f, address: { ...f.address, city: e.target.value } }))} />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField fullWidth label="State" value={form.address.state} onChange={(e) => setForm((f) => ({ ...f, address: { ...f.address, state: e.target.value } }))} />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField fullWidth label="Zip" value={form.address.zipCode} onChange={(e) => setForm((f) => ({ ...f, address: { ...f.address, zipCode: e.target.value } }))} />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField fullWidth label="Country" value={form.address.country} onChange={(e) => setForm((f) => ({ ...f, address: { ...f.address, country: e.target.value } }))} />
                </Grid>
              </Grid>
            ) : (
              <>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap', mb: 1 }}>
                  <Typography variant="h6" fontWeight={600}>
                    {school.name}
                  </Typography>
                  {school.onboardingStatus === 'ONBOARDED' && (
                    <Chip label="Onboarded" color="success" size="small" />
                  )}
                </Box>
                <Typography variant="body2" color="text.secondary" sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 2 }}>
                  <CodeIcon fontSize="small" /> School code: <strong>{school.uniqueCode}</strong>
                </Typography>
                <Grid container spacing={2}>
                  <Grid item xs={12} sm={6}>
                    <Typography variant="caption" color="text.secondary">Registered mobile</Typography>
                    <Typography sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                      <PhoneIcon fontSize="small" color="action" /> {displayMobile}
                    </Typography>
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <Typography variant="caption" color="text.secondary">Registered email</Typography>
                    <Typography sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                      <EmailIcon fontSize="small" color="action" /> {displayEmail}
                    </Typography>
                  </Grid>
                  <Grid item xs={12}>
                    <Typography variant="caption" color="text.secondary">Address</Typography>
                    <Typography sx={{ display: 'flex', alignItems: 'flex-start', gap: 0.5 }}>
                      <PlaceIcon fontSize="small" color="action" sx={{ mt: 0.25 }} /> {formatAddress(school.address)}
                    </Typography>
                  </Grid>
                </Grid>
              </>
            )}
          </Grid>
        </Grid>
      </Paper>

      <Paper sx={{ p: 3, mt: 3 }}>
        <Typography variant="h6" fontWeight={600} sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
          <LockIcon /> Change password
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          Update your login password. You will need to sign in again with the new password.
        </Typography>
        <form onSubmit={handleChangePassword}>
          <Grid container spacing={2}>
            <Grid item xs={12} sm={4}>
              <TextField
                fullWidth
                type="password"
                label="Current password"
                value={changePw.currentPassword}
                onChange={(e) => setChangePw((p) => ({ ...p, currentPassword: e.target.value }))}
                required
                autoComplete="current-password"
              />
            </Grid>
            <Grid item xs={12} sm={4}>
              <TextField
                fullWidth
                type="password"
                label="New password"
                value={changePw.newPassword}
                onChange={(e) => setChangePw((p) => ({ ...p, newPassword: e.target.value }))}
                required
                inputProps={{ minLength: 6 }}
                helperText="Min 6 characters"
                autoComplete="new-password"
              />
            </Grid>
            <Grid item xs={12} sm={4}>
              <TextField
                fullWidth
                type="password"
                label="Confirm new password"
                value={changePw.confirmPassword}
                onChange={(e) => setChangePw((p) => ({ ...p, confirmPassword: e.target.value }))}
                required
                error={!!changePw.confirmPassword && changePw.newPassword !== changePw.confirmPassword}
                helperText={changePw.confirmPassword && changePw.newPassword !== changePw.confirmPassword ? 'Does not match' : undefined}
                autoComplete="new-password"
              />
            </Grid>
            <Grid item xs={12}>
              <Button type="submit" variant="contained" disabled={changePwLoading || !changePw.currentPassword || changePw.newPassword.length < 6 || changePw.newPassword !== changePw.confirmPassword}>
                {changePwLoading ? 'Updating…' : 'Update password'}
              </Button>
            </Grid>
          </Grid>
        </form>
      </Paper>
    </Box>
  );
};

export default SchoolProfile;
